import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import ContinueButton from '@/shared-components/buttons/components/ContinueButton';

import './styles/GenericPopup.css';

let activePointerLocks = 0;
let previousRootPointerEvents;

function disableRootPointerEvents() {
  const root = document.getElementById('root');
  if (!root) {
    return null;
  }

  if (activePointerLocks === 0) {
    previousRootPointerEvents = root.style.pointerEvents;
    root.style.pointerEvents = 'none';
    root.setAttribute('aria-hidden', 'true');
  }

  activePointerLocks += 1;
  return root;
}

function restoreRootPointerEvents(root) {
  if (!root) {
    return;
  }

  activePointerLocks = Math.max(0, activePointerLocks - 1);
  if (activePointerLocks === 0) {
    if (previousRootPointerEvents) {
      root.style.pointerEvents = previousRootPointerEvents;
    } else {
      root.style.removeProperty('pointer-events');
    }
    root.removeAttribute('aria-hidden');
    previousRootPointerEvents = undefined;
  }
}

export default function GenericPopup({ open=false, onCloseAction, closeButtonText, closeDisabled=false, onConfirm, confirmButtonText, confirmDisabled=false, confirmLoading=false, children, customRef, animation=true, blur=false, darkerBlur=false, opacity=false, className="", backgroundClassName="", onBackgroundClick, ...events }) {
  const didIRemoveTheScroll = useRef(false);
  const hasDisabledRootPointerEvents = useRef(false);
  const cachedRootRef = useRef(null);

  useEffect(()=>{
    if(open === true) {
      if(!document.body.classList.contains("no-scroll")) {
        document.body.classList.add("no-scroll");
        didIRemoveTheScroll.current = true;
      }
    } else { 
      if(didIRemoveTheScroll.current) {
        document.body.classList.remove("no-scroll");
        didIRemoveTheScroll.current = false;
      }
    }
  }, [open]);

  useEffect(()=>{
    return () => {
      if(didIRemoveTheScroll.current) {
        document.body.classList.remove("no-scroll"); /** Prevents closed popups from removing this class when unmount */
      } 
    };
  }, []);

  useEffect(() => {
    if (open && !hasDisabledRootPointerEvents.current) {
      const rootElement = disableRootPointerEvents();
      if (rootElement) {
        cachedRootRef.current = rootElement;
        hasDisabledRootPointerEvents.current = true;
      }
    }

    return () => {
      if (hasDisabledRootPointerEvents.current) {
        restoreRootPointerEvents(cachedRootRef.current);
        hasDisabledRootPointerEvents.current = false;
        cachedRootRef.current = null;
      }
    };
  }, [open]);

  if(!open) {
    return (
      <></>
    );
  }

  const { onClick: forwardedOnClick, ...backgroundEvents } = events;

  function handleBackgroundClick(event) {
    if (event.target !== event.currentTarget) {
      return;
    }

    event.stopPropagation();

    if (typeof onBackgroundClick === 'function') {
      onBackgroundClick(event);
    }

    if (typeof forwardedOnClick === 'function') {
      forwardedOnClick(event);
    }
  }

  return createPortal(
    <>
      <div 
        className={
          `generic-popup__background
                    ${blur ? "blur" : ""}
                    ${darkerBlur ? "darker-blur" : ""}
                    ${opacity ? "opacity" : ""}
                    ${animation ? "enter-animation" : ""} 
                    ${backgroundClassName}`
        } 
        onClick={handleBackgroundClick}
        {...backgroundEvents} /** Although we are using portals, React mantains the same original tree for events. That's why we want to stopPropagating the clickEvent. So that it behaves as if the portal was truly real. In case we need to mantain the original event call stack, we should double check this */
      > 
        {/* Render children */}
        <div
          className={`generic-popup__card ${className}`}
          ref={customRef}
          role="dialog"
          aria-modal="true"
        >
          {children}
          
          {/* If onConfirm or onCloseAction provided, render footer buttons */}
          {(onConfirm || onCloseAction) && (
            <div className="generic-popup__footer element-non-selectable">
              {onCloseAction && (
                <ContinueButton
                  onClick={onCloseAction}
                  disabled={closeDisabled}
                  className="generic-popup__close-btn font-size-medium">
                  {closeButtonText || "Cancel"}
                </ContinueButton>
              )}
              {onConfirm && (
                <ContinueButton
                  onClick={onConfirm}
                  loading={confirmLoading}
                  turnOff={confirmDisabled}
                  className="generic-popup__confirm-btn font-size-medium">
                  {confirmButtonText || "OK"}
                </ContinueButton>
              )}
            </div>
          )}
        </div>
      </div>
    </>, document.getElementById("portal-generic-popup")
  );
}
